import { Navigate, useLocation } from 'react-router-dom'
import { useAuthStore, redirectToLogin, redirectToConsumerPortal } from '@personic/shared'
import { useImpersonationStore } from '@/stores/impersonationStore'

interface AuthGuardProps {
  children: React.ReactNode
  allowedRoles?: string[]
  requireActiveTenant?: boolean
}

export function AuthGuard({
  children,
  allowedRoles,
  requireActiveTenant = true,
}: AuthGuardProps) {
  const { user, isAuthenticated, tenantStatus, isInitializing } = useAuthStore()
  const location = useLocation()

  if (isInitializing) return null

  if (!isAuthenticated) {
    redirectToLogin(location.pathname)
    return null
  }

  const { isImpersonating } = useImpersonationStore()

  // Consumer role users on portal → redirect to my.personicai.com
  if (user?.role === 'consumer') {
    redirectToConsumerPortal('/dashboard')
    return null
  }

  const isStaff = user?.role === 'employee' || user?.role === 'admin' || user?.role === 'superuser'
  const isStaffImpersonating = isStaff && isImpersonating
  const hasNativeTenant = isStaff && !!user?.tenant_id

  // Section-level role checks for new routes
  const path = location.pathname
  if (path.startsWith('/admin/') || path === '/admin') {
    if (!isStaff) {
      return <Navigate to="/" replace />
    }
  }

  if (allowedRoles && user && !allowedRoles.includes(user.role) && !isStaffImpersonating && !hasNativeTenant) {
    return <Navigate to="/" replace />
  }

  if (requireActiveTenant && user?.role === 'tenant_admin') {
    if (tenantStatus === 'pending') {
      return <Navigate to="/tenant/pending" replace />
    }
    if (tenantStatus === 'suspended') {
      return <Navigate to="/tenant/suspended" replace />
    }
  }

  return <>{children}</>
}

export function StatusPageGuard({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user, isInitializing } = useAuthStore()
  const location = useLocation()

  if (isInitializing) return null

  if (!isAuthenticated) {
    redirectToLogin(location.pathname)
    return null
  }

  if (user?.role !== 'tenant_admin') {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}

export function DashboardRedirect() {
  const { user, isAuthenticated, tenantStatus, isInitializing } = useAuthStore()
  const location = useLocation()

  if (isInitializing) return null

  if (!isAuthenticated) {
    redirectToLogin(location.pathname)
    return null
  }

  // Consumer role → redirect to consumer portal
  if (user?.role === 'consumer') {
    redirectToConsumerPortal('/dashboard')
    return null
  }

  if (user?.role === 'employee' || user?.role === 'admin' || user?.role === 'superuser') {
    return <Navigate to="/employee/dashboard" replace />
  }

  if (tenantStatus === 'pending') {
    return <Navigate to="/tenant/pending" replace />
  }
  if (tenantStatus === 'suspended') {
    return <Navigate to="/tenant/suspended" replace />
  }

  return <Navigate to="/tenant/dashboard" replace />
}
